import '@molecules/subscription-box/subscription-box';

import { Component, Prop, Query, QueryAll } from '@atomify/core';
import { Fragment, h } from '@atomify/jsx';
import { Button, ButtonA } from '@atoms/button/button';
import { clearFocus, mapStore, setFocus } from '@bpd-library/components';
import { ExtendedConstructionNumber, isApartment, ProjectPhaseModel } from '@bpd-library/utilities';
import { ListSection } from '@features/wishlist/utilities/service';
import { ConstructionNumberCardElement } from '@molecules/construction-number-card/construction-number-card';
import { getSubscriptionState } from '@store/subscription/store';
import { isLoggedIn, login } from '@store/user';
import {
    formatConstructionNumberStatus,
    formatDetailPageUrl,
    formatLivingArea,
    formatPrice,
    formatProjectPhaseStatus,
    formatRooms,
    formatShoppingCartUrl,
    FormattedSpecification,
} from '@utilities/api-formatters';
import { isConstructionNumberNhg } from '@utilities/api-utilities/is-nhg';
import { GTMPush } from '@utilities/gtm';
import { translateItem, useTranslate } from '@utilities/hooks';
import kebabCase from '@utilities/kebab-case';

import { WishlistProjectHeading } from '../wishlist-project-heading/wishlist-project-heading';

const style = './wishlist-list.scss';

@Component({
    tag: 'bpd-wishlist-list',
    style,
})
export class WishlistList extends HTMLElement {
    userWishlist: string[] | undefined;
    loginButtonLabel: string;
    previousHoverCard: ConstructionNumberCardElement | undefined;

    @Prop({ reRender: true }) listData: ListSection[];
    @Prop() projectTotals: { [key: string]: Text } = {};

    @Query('[js-wishlist-list-container]') listContainer: HTMLElement;
    @QueryAll('[js-hook-wishlist-list-card]') cards: NodeListOf<ConstructionNumberCardElement>;

    async componentDidLoad() {
        try {
            this.loginButtonLabel = await translateItem('Login');
            this.renderList();
        } catch (error) {
            console.log(error);
        }

        this.subscribe();
    }

    subscribe() {
        mapStore.subscribe(
            ({ hover }) => {
                if (this.previousHoverCard) this.previousHoverCard.unsetHighlighted();

                if (hover) {
                    const card = Array.from(this.cards).find(({ id }) => id === hover.current?.id);

                    if (card) {
                        card.setHighlighted();
                        this.previousHoverCard = card;
                    }
                }
            },
            ['hover'],
        );
    }

    handleHover(constructionNumber: ExtendedConstructionNumber) {
        if (isApartment({ constructionNumber })) {
            const { apartmentBuilding } = constructionNumber;

            if (apartmentBuilding)
                setFocus([apartmentBuilding.id], {
                    zoom: true,
                });
        } else {
            setFocus([constructionNumber.id], {
                zoom: true,
            });
        }
    }

    clearHover() {
        clearFocus();
    }

    handleGTM(title: string, url: string) {
        GTMPush({
            event: 'gtm.Wishlist',
            eventCategory: 'Wishlist',
            eventAction: 'CTA_woningaanbod',
            eventLabel: title,
        });

        // Needed to give the browser enough time to push the event
        setTimeout(() => {
            window.location.href = url;
        }, 50);
    }

    renderSubscriptionBox(project: ProjectPhaseModel) {
        if (!project) return;

        const { id, districtId, name } = project;

        return (
            <subscription-box
                class="wishlist-list__subscription-box"
                variant="horizontal"
                boxTitle={useTranslate('Wishlist.SubscriptionBox-Title')}
                boxContent={useTranslate('Wishlist.SubscriptionBox-Subtitle', {
                    replace: [{ id: 'Project', value: name || '' }],
                })}
                projectPhaseId={id}
                projectPhaseName={name}
                districtId={districtId}
                buttonLabel={useTranslate('SubscriptionBox.ButtonLabel')}
                deleteWhenSubscribed={true}
            />
        );
    }

    renderList() {
        const subscribedProjects = getSubscriptionState().projectPhases || [];

        if (!this.listContainer) return;

        this.listContainer.innerHTML = '';

        this.listContainer.append(
            <Fragment>
                {this.listData.length > 0 ? (
                    this.listData.map((data) => {
                        const isSubscribedToProject = !!subscribedProjects.find(
                            (id) => id === data.project.id,
                        );

                        return (
                            <div class="wishlist-list__container">
                                <WishlistProjectHeading
                                    classes="wishlist-list__project"
                                    title={data.project.name || ''}
                                    status={data.project.status && kebabCase(data.project.status)}
                                    statusLabel={formatProjectPhaseStatus(data.project)?.label}
                                    total={
                                        data.project.name && this.projectTotals[data.project.name]
                                    }
                                />

                                {data.constructionNumbers.map((constructionNumber) => {
                                    const {
                                        propertyType,
                                        propertyTypeName,
                                        mainImage,
                                        identifier,
                                        areaProject,
                                    } = constructionNumber;

                                    const specs: FormattedSpecification[] = [];

                                    const rooms = formatRooms(constructionNumber);
                                    const livingArea = formatLivingArea(constructionNumber);

                                    if (rooms) specs.push(rooms);
                                    if (livingArea) specs.push(livingArea);

                                    return (
                                        <bpd-construction-number-card
                                            js-hook-wishlist-list-card
                                            class="wishlist-list__construction-number"
                                            houseName={`${propertyTypeName} #${identifier}`}
                                            onMouseEnter={() =>
                                                this.handleHover(constructionNumber)
                                            }
                                            onMouseLeave={() => this.clearHover()}
                                            onFocus={() => this.handleHover(constructionNumber)}
                                            id={constructionNumber.id}
                                            price={formatPrice({ constructionNumber })}
                                            href={formatDetailPageUrl({ constructionNumber })}
                                            status={formatConstructionNumberStatus(
                                                constructionNumber,
                                            )}
                                            isNhg={isConstructionNumberNhg(constructionNumber)}
                                            image={{
                                                url: mainImage?.url,
                                                alt: mainImage?.title,
                                            }}
                                            specs={specs}
                                            wishlistItemCount={constructionNumber.wishlistItemCount}
                                            shoppingCartUrl={formatShoppingCartUrl(
                                                constructionNumber,
                                            )}
                                            hidePrice={!propertyType?.canShowPrice}
                                            compare={
                                                !areaProject?.website?.disablePropertyComparison
                                            }
                                        />
                                    );
                                })}
                                {!isSubscribedToProject &&
                                    data.project.status !== 'Rented' &&
                                    data.project.status !== 'Sold' &&
                                    this.renderSubscriptionBox(data.project)}
                            </div>
                        );
                    })
                ) : (
                    <div class="wishlist-list__empty">
                        <bpd-svg name="icons-wishlist-empty" />
                        <span class="wishlist-list__title">
                            {useTranslate('NoHomesFoundTitle')}
                        </span>
                        {!isLoggedIn() && (
                            <span class="wishlist-list__text">
                                {useTranslate('LoginToSeeWishlist')}
                            </span>
                        )}
                        {isLoggedIn() && (
                            <ButtonA
                                classes="u-margin-top-2--md u-margin-top-1"
                                variant="tertiary"
                                type="button"
                                label={'Voeg bouwnummers toe'}
                                onClick={() =>
                                    this.handleGTM(
                                        'Voeg bouwnummers toe',
                                        window.__ENVIRONMENT_DETAILS__.areaHouseOverviewUrl,
                                    )
                                }
                            />
                        )}
                        {!isLoggedIn() && this.loginButtonLabel && (
                            <Button
                                classes="wishlist__login-button"
                                variant="tertiary"
                                type="button"
                                label={this.loginButtonLabel}
                                onClick={() => this.handleLogin()}
                            />
                        )}
                    </div>
                )}

                {this.listData.length > 0 && (
                    <div class="wishlist-list__more u-flex u-flex-middle u-flex-column">
                        <ButtonA
                            variant="tertiary"
                            type="button"
                            label={'Voeg meer toe'}
                            onClick={() =>
                                this.handleGTM(
                                    'Voeg meer toe',
                                    window.__ENVIRONMENT_DETAILS__.areaHouseOverviewUrl,
                                )
                            }
                        />
                    </div>
                )}
            </Fragment>,
        );
    }

    handleLogin() {
        if (!isLoggedIn()) {
            login();
        }
    }

    render() {
        return <div class="c-wishlist-list" js-wishlist-list-container></div>;
    }
}
